/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { BrandColors, MaterialIcon, PopoverList } from '@blend/enterprise-components';
import { css, StyleSheet } from 'aphrodite';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { addNotifications } from '../../../actions/pageActions';
import { notaryAPI } from '../../../api/notary';
import { NotaryAPI } from '../../../api/notary/types';
import { ConfirmationModal } from '../../../components/common/Modals/ConfirmationModal/ConfirmationModal';
import { NotificationItem } from '../../../components/common/Notifications/NotificationItem';
import { InfoCard } from '../../../components/form/InfoCard/InfoCard';
import { formatFullName, formatIsoDate } from '@utils/formatting';

export namespace AssignmentActions {
  export interface Props {
    room: NotaryAPI.RoomListItem;
    fetchRooms: () => Promise<void>;
  }
}

const styles = StyleSheet.create({
  actionsWrapper: {
    display: 'flex',
    width: '100%',
  },
  actionWrapper: {
    ':not(:last-child)': {
      paddingRight: 20,
    },
    ':last-child': {
      flexGrow: 1,
    },
  },
  notaryMenuButton: {
    border: 'none',
    background: 'none',
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'center',
  },
});

export const AssignmentActions: React.FC<AssignmentActions.Props> = ({ room, fetchRooms }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const popoverListButtonConfigs = [
    {
      label: 'Unassign from me',
      onClick: () => setIsOpen(true),
    },
  ];

  const unassignFromRoom = async () => {
    try {
      await notaryAPI.rooms.unassignRoom(room.id, room.notaryId!);
      await fetchRooms();
    } catch (err) {
      const notifications: NotificationItem.PropsWithoutId[] = [
        {
          type: 'error',
          iconType: 'error',
          content:
            (err as AxiosError<{ message?: string }>).response?.data?.message ||
            'That action could not be completed. Try again.',
          shouldAutoRemove: false,
        },
      ];
      addNotifications(notifications)(dispatch);
    }
  };

  const getUniqueSignersString = (signers: NotaryAPI.Signer[]) =>
    [
      ...new Set(
        signers.map(signer => {
          return formatFullName(signer);
        }),
      ),
    ].join(', ');
  const signers = getUniqueSignersString(room.signers);

  return (
    <div className={css(styles.actionsWrapper)}>
      <div className={css(styles.actionWrapper)}>
        <PopoverList anchorPosition={'bottom-end'} buttonConfigs={popoverListButtonConfigs} btnSize={'md'}>
          <button
            onClick={e => e.stopPropagation()}
            data-it={`action-popover-${room.id}`}
            className={css(styles.notaryMenuButton)}
          >
            <MaterialIcon type={'more_horiz'} size={24} color={BrandColors.NIGHT_90} />
          </button>
        </PopoverList>
      </div>
      <ConfirmationModal
        isOpen={isOpen}
        contentLabel="Unassign from me"
        handleClose={() => setIsOpen(false)}
        closeButtonText="Cancel"
        heading="Unassign from me"
        confirmButtonText="Unassign"
        description="This appointment will no longer be assigned to you."
        handleConfirm={unassignFromRoom}
        data-it="ConfirmationModal-Unassign"
      >
        <InfoCard
          cardInfo={[
            { primary: 'Date:', value: formatIsoDate(room.scheduleDate) },
            { primary: 'Signer(s):', value: signers },
            { primary: 'State:', value: room.state },
            { primary: 'Signing:', value: 'Purchase' },
          ]}
        />
      </ConfirmationModal>
    </div>
  );
};
